import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import { getToken, setToken } from '@/auth/tokenAccess';
import { getUserManager } from '@/auth/userManager';
import { AUTH_MODE, type AuthMode } from '@/auth/mode';
import { setForbidden } from '@/state/accessSlice';
import { isXtmCloudProblem } from '@/state/api/problemDetails';

/**
 * Per-endpoint options, set as `extraOptions` on an endpoint definition.
 *
 * `skipForbiddenRedirect` keeps a `403` from dispatching `setForbidden()`, which sends the whole app to the
 * access-denied page. It is for an endpoint whose `403` the calling screen handles itself, such as the style guide
 * editor's token request: the editor must stay open, read-only, with the administrator's unsaved edits intact.
 */
export interface ReauthExtraOptions {
  skipForbiddenRedirect?: boolean;
}

// Carry fetchBaseQuery's meta (request/response) through the wrapper so endpoints'
// transformResponse can still read headers (e.g. Retry-After) via `meta.response`.
type ReauthBaseQuery = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  ReauthExtraOptions,
  FetchBaseQueryMeta
>;

type QueryResult = Awaited<ReturnType<ReauthBaseQuery>>;

interface UserManagerLike {
  signinSilent: () => Promise<{ access_token: string } | null>;
  signinRedirect: () => Promise<void>;
}

interface ReauthDeps {
  rawBaseQuery: ReauthBaseQuery;
  getUserManager: () => UserManagerLike;
  mode: AuthMode;
}

export function createReauthBaseQuery(deps: ReauthDeps): ReauthBaseQuery {
  // Shared across concurrent calls so a burst of 401s triggers ONE silent renew
  // (and at most one redirect) instead of hammering the IdP.
  let renewPromise: Promise<{ access_token: string } | null> | null = null;
  let redirecting = false;

  // A freshly-renewed token is STILL rejected (missing required claim,
  // non-JWT/opaque access token, wrong audience, …). Logging in again
  // would mint another token that also fails, so do NOT redirect — that
  // would loop through the IdP. Surface it as a terminal access error.
  function rejectRenewedToken(api: Parameters<ReauthBaseQuery>[1]) {
    api.dispatch(setForbidden());
  }

  // No session to renew from → genuine re-login.
  async function redirectToLogin() {
    if (!redirecting) {
      redirecting = true;
      await deps.getUserManager().signinRedirect();
    }
  }

  /**
   * Silently renews the session and, when that yields a token, replays the request
   * once. `retried` is the replay's result, or null when there was no token to replay
   * with. `renewed` is set before the replay, so a replay that throws still counts as
   * a renewed token being rejected.
   */
  async function renewAndRetry(...[args, api, extraOptions]: Parameters<ReauthBaseQuery>) {
    let renewed = false;
    let retried: QueryResult | null = null;
    try {
      renewPromise ??= deps.getUserManager().signinSilent();
      const user = await renewPromise;
      if (user?.access_token) {
        setToken(user.access_token);
        renewed = true;
        retried = await deps.rawBaseQuery(args, api, extraOptions);
      }
    } catch {
      // silent renew failed — there is no valid session to refresh from
    } finally {
      renewPromise = null;
    }
    return { renewed, retried };
  }

  async function handleStillUnauthorized(
    result: QueryResult,
    renewed: boolean,
    api: Parameters<ReauthBaseQuery>[1],
  ) {
    if (result.error?.status !== 401) return;
    if (renewed) {
      rejectRenewedToken(api);
    } else {
      await redirectToLogin();
    }
  }

  return async (args, api, extraOptions) => {
    let result = await deps.rawBaseQuery(args, api, extraOptions);

    // An upstream XTM Cloud failure is not a problem with *this* session, even
    // when it arrives as 401: the backend proxies XTM Cloud, so a rejected
    // tenant password surfaces as `urn:problem-type:xtm-cloud.authentication`
    // with status 401. Renewing our token cannot fix that, and the retry fails
    // identically — which then looked like a freshly-renewed token being
    // rejected and ended on the "access denied" page. Hand it straight back so
    // the calling screen can say what actually needs doing.
    if (isXtmCloudProblem(result.error)) {
      return result;
    }

    if (result.error?.status === 401 && deps.mode === 'oidc') {
      const { renewed, retried } = await renewAndRetry(args, api, extraOptions);
      if (retried) {
        result = retried;
        // Re-check, because the retry is the first call that actually reaches
        // XTM Cloud: the unauthenticated first attempt never got past our own
        // backend, so an upstream problem can only surface here. Judging this
        // one on status alone reads it as "our fresh token was rejected" and
        // lands on /access-denied — the exact outcome the guard above exists
        // to prevent, one silent renew later.
        if (isXtmCloudProblem(result.error)) {
          return result;
        }
      }
      await handleStillUnauthorized(result, renewed, api);
    }

    if (result.error?.status === 403 && !extraOptions?.skipForbiddenRedirect) {
      api.dispatch(setForbidden());
    }

    return result;
  };
}

const rawBaseQuery: ReauthBaseQuery = fetchBaseQuery({
  baseUrl: '/api/v1',
  prepareHeaders: (headers) => {
    const token = getToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return headers;
  },
});

export const baseQueryWithReauth = createReauthBaseQuery({
  rawBaseQuery,
  getUserManager,
  mode: AUTH_MODE,
});
